import React, { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const ITEMS_PER_PAGE = 20;

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [id, setId] = useState(1);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const notificationSound = useRef(new Audio("/notify.mp3"));
  const prevUnreadCount = useRef(0);

  const role = localStorage.getItem("role");
  const token =
    role === "admin"
      ? localStorage.getItem("adminToken")
      : localStorage.getItem("userToken");

  const fetchNotifications = useRef(null);

  useEffect(() => {
    fetchNotifications.current = async () => {
      console.log("useeffect call");
      try {
        setLoading(true);
        const role = localStorage.getItem("role");
        const res = await axios.get(
          `${BASE_URL}/api/notification/${id}/${role}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const sortedData = Array.isArray(res.data)
          ? [...res.data].sort(
              (a, b) =>
                new Date(b.notificationAssignTime || 0) -
                new Date(a.notificationAssignTime || 0)
            )
          : [];

        setNotifications(sortedData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
      console.log("notification received");
    };
  }, [token]);

  useEffect(() => {
    fetchNotifications.current?.();
  }, []);

  const unreadCount = notifications.filter((n) => !n.notificationStatus).length;

  useEffect(() => {
    if (unreadCount > prevUnreadCount.current) {
      notificationSound.current.play().catch(() => {});
    }
    prevUnreadCount.current = unreadCount;
  }, [unreadCount]);

  const filteredData = useMemo(() => {
    const text = search.trim().toLowerCase();

    return notifications.filter((n) => {
      const searchMatch = text
        ? [
            n.notificationMessage,
            n.notificationSenderName,
            n.notificationSender,
            n.notificationStatus ? "read" : "unread",
          ].some((f) => String(f || "").toLowerCase().includes(text))
        : true;

      const statusMatch = statusFilter
        ? statusFilter === "read"
          ? n.notificationStatus
          : !n.notificationStatus
        : true;

      return searchMatch && statusMatch;
    });
  }, [notifications, search, statusFilter]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const formatDate = (d) => {
    if (!d) return "--";
    const date = new Date(d);
    return isNaN(date)
      ? "--"
      : date.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.get(
        `${BASE_URL}/api/notification/read/${role}/${notificationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      notificationSound.current.play().catch(() => {});
      fetchNotifications.current?.();
    } catch (err) {
      console.error("Mark read failed", err);
    }
  };

  const handleMarkAllAsRead = async () => {
    const unread = notifications.filter((n) => !n.notificationStatus);

    try {
      await Promise.all(
        unread.map((n) =>
          axios.get(
            `${BASE_URL}/api/notification/read/all/${role}/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );

      notificationSound.current.play().catch(() => {});
      fetchNotifications.current?.();
    } catch (err) {
      console.error("Mark all read failed", err);
    }
  };

  return (
    <>
      <div className="notifications-wrapper">
        {/* Header */}
        <header className="notifications-header">
          <div className="header-content">
            <div className="header-title">
              <h1>Notifications</h1>
              <p>Stay updated with system alerts and messages</p>
            </div>
            <div className="header-actions">
              <button onClick={() => fetchNotifications.current?.()} className="action-btn refresh">
                <i className="fas fa-sync-alt"></i>
                <span>Refresh</span>
              </button>
              {unreadCount > 0 && (
                <button onClick={handleMarkAllAsRead} className="action-btn success">
                  <i className="fas fa-check-double"></i>
                  <span>Mark All Read</span>
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="notifications-main">
          {/* Filters */}
          <section className="filters-card">
            <div className="search-group">
              <i className="fas fa-search"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search messages, sender..."
                className="search-input"
              />
            </div>

            <div className="filter-group">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="status-select"
              >
                <option value="">All Status</option>
                <option value="unread">Unread Only</option>
                <option value="read">Read Only</option>
              </select>

              <div className="stats">
                <span>Total: {notifications.length}</span>
                <span>Unread: {unreadCount}</span>
                <span>Read: {notifications.length - unreadCount}</span>
              </div>
            </div>
          </section>

          {/* Notifications List */}
          <section className="list-card">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading notifications...</p>
              </div>
            ) : paginatedData.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-bell-slash"></i>
                <h3>No notifications found</h3>
                <p>Try adjusting filters or check back later.</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <div className="table-header">
                    <div>#</div>
                    <div>Message</div>
                    <div>Received</div>
                    <div>Read Time</div>
                    <div>Status</div>
                  </div>

                  {paginatedData.map((n, i) => (
                    <div
                      key={n.notificationId}
                      className={`table-row ${!n.notificationStatus ? "unread" : ""}`}
                    >
                      <div>{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</div>
                      <div className="message-cell">
                        <strong>{n.notificationMessage}</strong>
                        <small>From: {n.notificationSenderName || "System"}</small>
                      </div>
                      <div>{formatDate(n.notificationAssignTime)}</div>
                      <div>{formatDate(n.notificationReadTime)}</div>
                      <div>
                        {n.notificationStatus ? (
                          <span className="status read">Read</span>
                        ) : (
                          <button
                            onClick={() => handleMarkAsRead(n.notificationId)}
                            className="mark-read-btn"
                          >
                            Mark as Read
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="page-btn"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`page-btn ${currentPage === pageNum ? "active" : ""}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="page-btn"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </main>
      </div>

      <style jsx>{`
        .notifications-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 0;
          overflow-x: hidden;
        }

        .notifications-header {
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(59, 130, 246, 0.2);
          padding: 1.5rem 2rem;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-content {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .header-title h1 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #60a5fa;
          margin: 0;
        }

        .header-title p {
          margin: 0.3rem 0 0;
          opacity: 0.85;
          font-size: 0.95rem;
        }

        .header-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .action-btn {
          padding: 0.7rem 1.3rem;
          border-radius: 10px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .action-btn.refresh {
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
        }

        .action-btn.success {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
        }

        .notifications-main {
          padding: 1.5rem 2rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        .filters-card {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          border: 1px solid rgba(59, 130, 246, 0.3);
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;
          align-items: end;
        }

        .search-group {
          position: relative;
          flex: 1;
          min-width: 280px;
        }

        .search-group i {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-input {
          width: 100%;
          padding: 0.9rem 1rem 0.9rem 2.8rem;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          font-size: 1rem;
        }

        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
        }

        .filter-group {
          display: flex;
          gap: 1.5rem;
          align-items: center;
          flex-wrap: wrap;
        }

        .status-select {
          padding: 0.9rem 1.2rem;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          min-width: 180px;
        }

        .stats {
          display: flex;
          gap: 1.2rem;
          color: #94a3b8;
          font-size: 0.95rem;
        }

        .stats span {
          background: rgba(15, 23, 42, 0.5);
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
        }

        .list-card {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 1.5rem;
          border: 1px solid rgba(59, 130, 246, 0.3);
          overflow: hidden;
        }

        .table-container {
          border-radius: 12px;
          overflow: hidden;
          background: rgba(15, 23, 42, 0.4);
        }

        .table-header {
          display: grid;
          grid-template-columns: 70px 2fr 1.2fr 1.2fr 1.2fr;
          padding: 1rem 1.5rem;
          background: rgba(59, 130, 246, 0.15);
          font-weight: 600;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .table-row {
          display: grid;
          grid-template-columns: 70px 2fr 1.2fr 1.2fr 1.2fr;
          padding: 1.2rem 1.5rem;
          border-bottom: 1px solid rgba(100, 116, 139, 0.2);
          transition: all 0.3s ease;
        }

        .table-row:hover {
          background: rgba(59, 130, 246, 0.08);
        }

        .table-row.unread {
          background: rgba(59, 130, 246, 0.12);
          border-left: 4px solid #3b82f6;
        }

        .message-cell {
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
        }

        .message-cell strong {
          color: white;
        }

        .message-cell small {
          color: #94a3b8;
          font-size: 0.85rem;
        }

        .status.read {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
          padding: 0.4rem 0.9rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .mark-read-btn {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.3s;
        }

        .mark-read-btn:hover {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .loading-state, .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #94a3b8;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #1e293b;
          border-top: 4px solid #60a5fa;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-state i {
          font-size: 3.5rem;
          opacity: 0.4;
          margin-bottom: 1rem;
        }

        .pagination {
          display: flex;
          justify-content: center;
          gap: 0.6rem;
          padding: 1.5rem 0;
          border-top: 1px solid rgba(100, 116, 139, 0.2);
        }

        .page-btn {
          width: 40px;
          height: 40px;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.4);
          border-radius: 10px;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s;
        }

        .page-btn:hover:not(:disabled) {
          background: #3b82f6;
        }

        .page-btn.active {
          background: #3b82f6;
          border-color: #3b82f6;
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .table-header, .table-row {
            grid-template-columns: 60px 1fr;
            gap: 1rem;
          }

          .table-header > div:nth-child(n+3),
          .table-row > div:nth-child(n+3) {
            grid-column: 1 / -1;
            margin-left: 60px;
          }

          .table-header {
            display: none;
          }

          .table-row > div {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .table-row > div::before {
            content: attr(data-label);
            font-weight: 600;
            color: #60a5fa;
            min-width: 120px;
          }

          .table-row > div:nth-child(1)::before { content: "#"; }
          .table-row > div:nth-child(2)::before { content: "Message"; }
          .table-row > div:nth-child(3)::before { content: "Received"; }
          .table-row > div:nth-child(4)::before { content: "Read Time"; }
          .table-row > div:nth-child(5)::before { content: "Status"; }
        }

        @media (max-width: 768px) {
          .notifications-header, .notifications-main {
            padding: 1rem;
          }

          .filters-card {
            flex-direction: column;
            align-items: stretch;
          }

          .filter-group {
            flex-direction: column;
            align-items: stretch;
          }

          .stats {
            justify-content: center;
          }
        }
      `}</style>
    </>
  );
}

// import React, { useEffect, useRef, useState, useMemo } from "react";
// import axios from "axios";

// const BASE_URL = process.env.REACT_APP_BASE_URL;
// const ITEMS_PER_PAGE = 20;

// export default function Notifications() {
//   const [notifications, setNotifications] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [id, setId] = useState(1);

//   const [search, setSearch] = useState("");
//   const [statusFilter, setStatusFilter] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);

//   const notificationSound = useRef(new Audio("/notify.mp3"));
//   const prevUnreadCount = useRef(0);

//   const role = localStorage.getItem("role");
//   const token =
//   role === "admin"
//   ? localStorage.getItem("adminToken")
//   : localStorage.getItem("userToken");
  
//   const fetchNotifications = useRef(null);
  
//   useEffect(() => {
//     fetchNotifications.current = async () => {
//       console.log("useeffect call")
//       try {
//         setLoading(true);
//         const role = localStorage.getItem("role");
//         const res = await axios.get(
//          `${BASE_URL}/api/notification/${id}/${role}`,
//           { headers: { Authorization: `Bearer ${token}` } }
//         );

//         const sortedData = Array.isArray(res.data)
//           ? [...res.data].sort(
//             (a, b) =>
//               new Date(b.notificationAssignTime || 0) -
//               new Date(a.notificationAssignTime || 0)
//           )
//           : [];

//         setNotifications(sortedData);
//       } catch (err) {
//                console.error(err);
//       } finally {
//         setLoading(false);
//       }
//       console.log("notification received")
//     };
//   }, [token]);

//   useEffect(() => {
//     fetchNotifications.current?.();
//   }, []);

//   const unreadCount = notifications.filter(
//     (n) => !n.notificationStatus
//   ).length;

//   useEffect(() => {
//     if (unreadCount > prevUnreadCount.current) {
//       notificationSound.current.play().catch(() => { });
//     }
//     prevUnreadCount.current = unreadCount;
//   }, [unreadCount]);

//   const filteredData = useMemo(() => {
//     const text = search.trim().toLowerCase();

//     return notifications.filter((n) => {
//       const searchMatch = text
//         ? [
//           n.notificationMessage,
//           n.notificationSenderName,
//           n.notificationSender,
//           n.notificationStatus ? "read" : "unread",
//         ].some((f) =>
//           String(f || "").toLowerCase().includes(text)
//         )
//         : true;

//       const statusMatch = statusFilter
//         ? statusFilter === "read"
//           ? n.notificationStatus
//           : !n.notificationStatus
//         : true;

//       return searchMatch && statusMatch;
//     });
//   }, [notifications, search, statusFilter]);

//   const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

//   const paginatedData = filteredData.slice(
//     (currentPage - 1) * ITEMS_PER_PAGE,
//     currentPage * ITEMS_PER_PAGE
//   );

//   const formatDate = (d) => {
//     if (!d) return "--";
//     const date = new Date(d);
//     return isNaN(date)
//       ? "--"
//       : date.toLocaleString("en-IN", {
//         day: "2-digit",
//         month: "short",
//         year: "numeric",
//         hour: "2-digit",
//         minute: "2-digit",
//         hour12: true,
//       });
//   };

//   const handleMarkAsRead = async (notificationId) => {
//     try {
//       await axios.get(
//         `${BASE_URL}/api/notification/read/${role}/${notificationId}`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       notificationSound.current.play().catch(() => { });
//       fetchNotifications.current?.();
//     } catch (err) {
//       console.error("Mark read failed", err);
//     }
//   };

//   const handleMarkAllAsRead = async () => {
//     const unread = notifications.filter(
//       (n) => !n.notificationStatus
//     );

//     try {
//       await Promise.all(
//         unread.map((n) =>
//           axios.get(
//             `${BASE_URL}/api/notification/read/all/${role}/${id}`,
//             { headers: { Authorization: `Bearer ${token}` } }
//           )
//         )
//       );

//       notificationSound.current.play().catch(() => { });
//       fetchNotifications.current?.();
//     } catch (err) {
//       console.error("Mark all read failed", err);
//     }
//   };

//   return (
//     <div style={styles.page}>
//       <h2 style={styles.title}>
//         System Notifications ({unreadCount})
//       </h2>

//       {/* FILTERS */}
//       <div style={styles.filterRow}>
//         <div style={styles.glassCard}>
//           <label>Search</label>
//           <input
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//             style={styles.input}
//             placeholder="Search..."
//           />
//         </div>

//         <div style={styles.glassCard}>
//           <label>Status</label>
//           <select
//             value={statusFilter}
//             onChange={(e) => setStatusFilter(e.target.value)}
//             style={styles.input}
//           >
//             <option value="">All</option>
//             <option value="read">Read</option>
//             <option value="unread">Unread</option>
//           </select>
//         </div>

//         <div style={{ display: "flex", gap: 10, alignItems: "end" }}>
//           <button style={styles.primaryBtn} onClick={() => fetchNotifications.current?.()}>
//             Refresh
//           </button>
//           <button style={styles.successBtn} onClick={handleMarkAllAsRead}>
//             Mark All Read
//           </button>
//         </div>
//       </div>

//       {/* TABLE */}
//       <div style={styles.tableCard}>
//         {loading ? (
//           <div style={styles.noData}>Loading...</div>
//         ) : paginatedData.length === 0 ? (
//           <div style={styles.noData}>No Notifications Found</div>
//         ) : (
//           <table style={styles.table}>
//             <thead>
//               <tr>
//                 {/* {["#", "Message", "Sender", "Role", "Read By", "Receive", "Read", "Action"].map((h) => ( */}
//                 {["#", "Message",  "Receive", "Read", "Action"].map((h) => (
//                   <th key={h} style={styles.th}>{h}</th>
//                 ))}
//               </tr>
//             </thead>
//             <tbody>
//               {paginatedData.map((n, i) => {
//                 const statusStyle = n.notificationStatus
//                   ? styles.statusActive
//                   : styles.statusOther;
//                 return (
//                   <tr key={n.notificationId}>
//                     <td style={styles.td}>{i + 1}</td>
//                     <td style={styles.td}>{n.notificationMessage}</td>
//                     {/* <td style={styles.td}>{n.notificationSenderName || "System"}</td> */}
//                     {/*   <td style={styles.td}>{n.notificationSender}</td> */}
//                     {/* <td style={styles.td}>
//                       <span style={statusStyle}>
//                         {`${n.notificationReadBy === "admin"
//                           ? "Admin"
//                           : n.notificationReadBy === "user"
//                             ? "Manager"
//                             : "Pending"
//                           }`}

//                       </span>
//                     </td> */}
//                     <td style={styles.td}>{formatDate(n.notificationAssignTime)}</td>
//                     <td style={styles.td}>{formatDate(n.notificationReadTime)}</td>
//                     <td style={styles.td}>
//                       {n.notificationStatus ? (
//                         <span style={{ color: "#888" }}>Completed</span>
//                       ) : (
//                         <button
//                           style={styles.acceptBtn}
//                           onClick={() =>
//                             handleMarkAsRead(n.notificationId)
//                           }
//                         >
//                           Mark Read
//                         </button>
//                       )}
//                     </td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         )}
//       </div>
//     </div>
//   );
// }

// /* ================= UI STYLES ONLY ================= */

// const styles = {
//   page: {
//     minHeight: "100vh",
//     padding: 30,
//     background:
//       "linear-gradient(135deg, rgba(145,195,243,0.7), rgba(233,239,125,0.9))",
//   },

//   title: {
//     fontSize: 30,
//     fontWeight: 800,
//     marginBottom: 25,
//     color: "#1967d2",
//   },

//   filterRow: {
//     display: "flex",
//     gap: 20,
//     marginBottom: 25,
//   },

//   glassCard: {
//     flex: 1,
//     padding: 16,
//     borderRadius: 12,
//     background: "rgba(255,255,255,0.45)",
//     backdropFilter: "blur(8px)",
//     boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
//   },

//   input: {
//     width: "100%",
//     marginTop: 6,
//     padding: 10,
//     borderRadius: 8,
//     border: "1.5px solid #1a73e8",
//   },

//   tableCard: {
//     background: "rgba(255,255,255,0.7)",
//     backdropFilter: "blur(8px)",
//     borderRadius: 15,
//     padding: 10,
//     boxShadow: "0 6px 25px rgba(0,0,0,0.15)",
//   },

//   table: { width: "100%", borderCollapse: "collapse" },

//   th: {
//     background: "#f4f4f6",
//     padding: 14,
//     fontWeight: 700,
//   },

//   td: {
//     padding: 14,
//     borderBottom: "1px solid #eee",
//   },

//   read: {
//     background: "#e6ffe6",
//     padding: "5px 12px",
//     borderRadius: 20,
//     color: "#2e7d32",
//   },

//   unread: {
//     background: "#fff8e1",
//     padding: "5px 12px",
//     borderRadius: 20,
//     color: "#ff9800",
//   },

//   primaryBtn: {
//     background: "#1a73e8",
//     color: "#fff",
//     border: "none",
//     padding: "10px 16px",
//     borderRadius: 8,
//     cursor: "pointer",
//   },

//   successBtn: {
//     background: "#2e7d32",
//     color: "#fff",
//     border: "none",
//     padding: "10px 16px",
//     borderRadius: 8,
//     cursor: "pointer",
//   },

//   noData: {
//     padding: 40,
//     textAlign: "center",
//     fontSize: 18,
//     color: "#666",
//   },

//   acceptBtn: {
//     background: "#2e7d32",
//     color: "#fff",
//     border: "none",
//     padding: "10px 16px",
//     borderRadius: 8,
//     cursor: "pointer",
//   }
// };
